import { useIsExperimentalUser } from '@/authenticated/hooks/useIsExperimentalUser'
import { defaultConfig, remoteConfig } from '@/firebase'
import { useAppSelector } from '@/hooks'

type Options = {
  flag: keyof typeof defaultConfig
  // When provided, the flag will have the value of experimentalValue if the user is an experimental user
  experimentalValue?: boolean
  // When true, `flag` is read as an integer rollout percentage (0-100) from
  // remote config and the user is included in the rollout iff
  // (userId % 100) < percentage. Mirrors the backend
  // RemoteSettings.is_simple_rollout_enabled. An absent or 0 percentage rolls
  // no one in (0% default).
  rollout?: boolean
}

// Percentage-based rollout keyed on a stable integer id (the user id), matching
// the backend's RemoteSettings.is_simple_rollout_enabled:
//   enabled_percentage > 0 and entity_id % 100 < enabled_percentage
// Defaults to 0% when the percentage is unset or the user id is unavailable.
function isUserInRollout(
  flag: keyof typeof defaultConfig,
  userId: number | undefined,
): boolean {
  const percentage = remoteConfig?.getNumber(flag) ?? 0
  if (percentage <= 0 || userId === undefined) {
    return false
  }
  return userId % 100 < percentage
}

export function useFeatureFlag(options: Options) {
  const isExperimentalUser = useIsExperimentalUser()
  const userId = useAppSelector((state) => state.user.user?.id)

  const value = options.rollout
    ? isUserInRollout(options.flag, userId)
    : remoteConfig?.getBoolean(options.flag) ?? false

  if (options.experimentalValue !== undefined) {
    return isExperimentalUser ? options.experimentalValue : value
  }

  return value
}
